<template>
<div class="calendar">
  <div class="day-select-row">
    <div class="month"></div>
    <div class="day-header">M</div>
    <div class="day-header">T</div>
    <div class="day-header">W</div>
    <div class="day-header">H</div>
    <div class="day-header">F</div>
    <div class="day-header">S</div>
    <div class="day-header">S</div>
  </div>
  <div class="day-select-row" v-for="(week, i) in daySelectByWeeks" :key="formatFullDate(week[0].date)">
    <div class="month">
      {{ getMonthNameForWeekIfFirst(i, week) }}
    </div>
    <div class="day-btn" v-for="daySelect in week" :key="formatFullDate(daySelect.date)"
      :class="{selected: isDaySelected(daySelect.date),
               chosen: isDayChosen(daySelect.date),
               disabled: isDayDisabled(daySelect.date),
               empty: daySelect.numSlots === 0}"
              @click="handleDayClicked(daySelect.date)">
      {{ formatDayNumber(daySelect.date) }}
    </div>
  </div>
</div>
</template>

<script>
import {ref, toRef, toRefs, watch} from "@vue/composition-api";
import {DateTime, Duration} from "luxon";
import {getScheduleLookup} from "@/model/scheduleAPI";


class DaySelect {
  constructor(date, numSlots) {
    this.date = date;
    this.numSlots = numSlots;
  }
}


export default {
  name: "CalendarDaySelect",
  props: {
    studioId: {type: String},
    selectedDay: {type: Object},

    timeSlots: {type: Array},
    selectedTimes: {type: Array},
  },
  setup(props, {emit}) {
    const {studioId, timeSlots, selectedTimes} = toRefs(props);
    const selectedDayProp = toRef(props, 'selectedDay');

    const selectedDay = ref(props.selectedDay);
    watch(selectedDayProp, (val) => {
      selectedDay.value = val;
    });
    const daySelectByWeeks = ref();

    // need the schedule lookup to highlight days
    let scheduleLookup = getScheduleLookup(timeSlots.value);

    function computeMostRecentMonday(toDate) {
      const MONDAY = 1;
      //const TODAY = DateTime.fromObject({hour: 0, minute: 0, second: 0});
      // Start out at toDate and work back to most recent Monday
      let d = toDate;
      while (d.weekday !== MONDAY) {
        d = d.minus({ days: 1 });
      }
      return d;
    }


    function formatDayNumber(d) {
      return d.day;
    }

    function formatFullDate(d) {
      return d.toISO();
    }
    function formatMonthName(d) {
      return d.monthLong;
    }

    function getMonthNameForWeekIfFirst(rowIndex, days) {
      // If first row, always show the month of the last day
      if (rowIndex === 0) {
        return formatMonthName(days[6].date);
      }
      // otherwise only show if transitioning to a new month
      else if (days[0].date.day > days[6].date.day) {
        return formatMonthName(days[6].date);
      }
      // Or if the 1st is a Monday...
      else if (days[0].date.day === 1) {
        return formatMonthName(days[6].date);
      }
      // Blank for other rows
      return "";
    }

    function refreshDaySelectButtons() {
      // finds what weeks to show on start date calendar
      //  computes the availabilities of each day
      const fromDate = scheduleLookup.getEarliestOpeningDate(); //DateTime.fromObject({hour: 0, minute: 0, second: 0});
      const mostRecentMonday = computeMostRecentMonday(fromDate);
      const NUM_WEEKS = 4;

      let weeks = [];
      let weekdays = [];
      for (let i = 0; i < (NUM_WEEKS * 7); i++) {
        const d = mostRecentMonday.plus(Duration.fromObject({days: i}));
        const roomSlots = scheduleLookup.findOpenings(studioId.value, d);
        let numSlots = 0;
        roomSlots.forEach((v) => {
          v.forEach((s => {
            if (s.status === 'available') {
              numSlots += 1;
            }
          }))
        })
        weekdays.push(new DaySelect(d, numSlots));
        if (i % 7 === 6) {
          weeks.push(weekdays);
          weekdays = [];
        }
      }

      daySelectByWeeks.value = weeks;
    }

    watch(studioId, refreshDaySelectButtons);
    refreshDaySelectButtons();


    function isDaySelected(d) {
      return selectedDay.value !== null && d.month === selectedDay.value.month && d.day === selectedDay.value.day
    }
    function isDayChosen(d) {
      const matchingTime = selectedTimes.value.find((time) => {
        const chosenDTDay = DateTime.fromISO(time.slot.date).startOf('day');
        return (chosenDTDay <= d
          && chosenDTDay.weekday === d.weekday
          && ""+time.slot.studioID === ""+studioId.value
        )
      })
      return matchingTime !== undefined;
    }
    function isDayDisabled(d) {
      const today = DateTime.fromObject({hour: 0, minute: 0, second: 0});
      return d.toMillis() <= today.toMillis();
    }
    function handleDayClicked(d) {
      if (!isDayDisabled(d)) {
        selectedDay.value = d;
        emit('update:selected-day', d);
      }
    }

    return {
      daySelectByWeeks,

      isDayDisabled,
      isDaySelected,
      isDayChosen,
      handleDayClicked,

      getMonthNameForWeekIfFirst,
      formatDayNumber,
      formatFullDate,
    }
  }
}
</script>

<style scoped>
.calendar {
  /*max-width: 85%;*/
  /*padding-left: 15%;*/
}
.day-select-row {
  padding-top: .5rem;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
}
.day-select-row:first-of-type {
  padding-top: 0;
}
.month {
  flex: 0 1 120px;

  line-height: 1.9rem;
  text-transform: uppercase;
  text-align: right;
  font-weight: bold;
  font-size: 1.1rem;
  color: #595959;
}
.day-btn {
  flex: 0 1 30px;
  cursor: pointer;
  user-select: none;
  width: 30px;
  height: 30px;
  line-height: 27px;
  text-align: center;
  font-size: 1rem;
  border-radius: 15px;
  border: 1px solid #595959;
}
.day-header {
  flex: 0 1 30px;
  cursor: pointer;
  user-select: none;
  width: 30px;
  height: 19px;
  line-height: 27px;
  text-align: center;
  font-size: .9rem;
  font-weight: bold;
}
.day-btn.chosen {
  color: #A72D32;
  border: 1px solid #A72D32;
  border-radius: 15px;
}
.day-btn.selected {
  background-color: #A72D32;
  border-color: #A72D32;
  color: white;
  font-weight: bold;
}
.day-btn.selected.chosen {
  /*color: white;*/
}
.day-btn.disabled {
  cursor: not-allowed;
  opacity: .3;
  border: 1px solid white;
}
.day-btn.has-availabilities {

}
.day-btn.empty {
  border: 1px solid white;
}
</style>